import React from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../ui/dialog';

const JOB_TITLES = [
  { value: 'HHA', label: 'Home Health Aide' },
  { value: 'PCA', label: 'Personal Care Aide' },
  { value: 'RN', label: 'Registered Nurse' },
  { value: 'LPN', label: 'Licensed Practical Nurse' },
  { value: 'STNA', label: 'State Tested Nursing Assistant' },
  { value: 'DSP', label: 'Direct Support Professional' },
];

const CERTIFICATION_TYPES = [
  { value: 'STNA', label: 'STNA' },
  { value: 'HHA', label: 'Home Health Aide' },
  { value: 'CPR', label: 'CPR Certified' },
  { value: 'First Aid', label: 'First Aid' },
];

export const EmployeeFormDialog = ({ open, onOpenChange, formData, onChange, onSubmit, editing, saving }) => {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle style={{ fontFamily: 'Manrope' }}>{editing ? 'Edit Employee' : 'Add New Employee'}</DialogTitle>
          <DialogDescription>Enter the employee's information for EVV and billing</DialogDescription>
        </DialogHeader>

        <form onSubmit={onSubmit} className="space-y-6">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2"><Label>First Name *</Label><Input value={formData.first_name} onChange={(e) => onChange('first_name', e.target.value)} required data-testid="employee-first-name" /></div>
            <div className="space-y-2"><Label>Last Name *</Label><Input value={formData.last_name} onChange={(e) => onChange('last_name', e.target.value)} required data-testid="employee-last-name" /></div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2"><Label>Employee ID *</Label><Input value={formData.employee_id} onChange={(e) => onChange('employee_id', e.target.value)} className="font-mono" required data-testid="employee-id" /></div>
            <div className="space-y-2"><Label>SSN Last 4</Label><Input value={formData.ssn_last_four} onChange={(e) => onChange('ssn_last_four', e.target.value)} maxLength={4} placeholder="****" className="font-mono" data-testid="employee-ssn" /></div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Full SSN (for Sandata EVV)</Label>
              <Input value={formData.ssn_full} onChange={(e) => onChange('ssn_full', e.target.value.replace(/\D/g, '').slice(0, 9))} maxLength={9} placeholder="9 digits, no dashes" className="font-mono" type="password" data-testid="employee-ssn-full" />
              <p className="text-xs text-slate-400">Required by Sandata if no NPI or Medicaid ID</p>
            </div>
            <div className="space-y-2"><Label>Date of Birth</Label><Input type="date" value={formData.date_of_birth} onChange={(e) => onChange('date_of_birth', e.target.value)} data-testid="employee-dob" /></div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2"><Label>Hire Date *</Label><Input type="date" value={formData.hire_date} onChange={(e) => onChange('hire_date', e.target.value)} required data-testid="employee-hire-date" /></div>
          </div>

          <div className="border-t pt-4">
            <h3 className="font-semibold text-slate-900 mb-4">Contact Information</h3>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2"><Label>Phone</Label><Input type="tel" value={formData.phone} onChange={(e) => onChange('phone', e.target.value)} placeholder="(555) 555-5555" data-testid="employee-phone" /></div>
              <div className="space-y-2"><Label>Email</Label><Input type="email" value={formData.email} onChange={(e) => onChange('email', e.target.value)} data-testid="employee-email" /></div>
            </div>
          </div>

          <div className="border-t pt-4">
            <h3 className="font-semibold text-slate-900 mb-4">Job Information</h3>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Job Title *</Label>
                <Select value={formData.job_title || 'select-role'} onValueChange={(val) => onChange('job_title', val === 'select-role' ? '' : val)}>
                  <SelectTrigger data-testid="employee-job-title"><SelectValue placeholder="Select role" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="select-role" disabled>Select role</SelectItem>
                    {JOB_TITLES.map((j) => <SelectItem key={j.value} value={j.value}>{j.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2"><Label>Hourly Rate</Label><Input type="number" step="0.01" value={formData.hourly_rate} onChange={(e) => onChange('hourly_rate', e.target.value)} placeholder="0.00" data-testid="employee-rate" /></div>
            </div>
            <div className="grid grid-cols-2 gap-4 mt-4">
              <div className="space-y-2">
                <Label>Certification Type</Label>
                <Select value={formData.certification_type || 'none'} onValueChange={(val) => onChange('certification_type', val === 'none' ? '' : val)}>
                  <SelectTrigger data-testid="employee-cert-type"><SelectValue placeholder="Select certification" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">None</SelectItem>
                    {CERTIFICATION_TYPES.map((c) => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2"><Label>Certification Expiry</Label><Input type="date" value={formData.certification_expiry} onChange={(e) => onChange('certification_expiry', e.target.value)} data-testid="employee-cert-expiry" /></div>
            </div>
            <div className="grid grid-cols-2 gap-4 mt-4">
              <div className="space-y-2"><Label>NPI</Label><Input value={formData.npi} onChange={(e) => onChange('npi', e.target.value)} placeholder="10-digit NPI" className="font-mono" data-testid="employee-npi" /></div>
              <div className="space-y-2"><Label>Employee Medicaid ID</Label><Input value={formData.employee_medicaid_id} onChange={(e) => onChange('employee_medicaid_id', e.target.value)} placeholder="Ohio Medicaid provider ID" className="font-mono" data-testid="employee-medicaid-id" /></div>
            </div>
            <div className="grid grid-cols-2 gap-4 mt-4">
              <div className="space-y-2"><Label>Sandata Employee ID</Label><Input value={formData.sandata_employee_id} onChange={(e) => onChange('sandata_employee_id', e.target.value)} placeholder="For EVV integration" className="font-mono" data-testid="employee-sandata-id" /></div>
            </div>
            <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
              <p className="text-xs text-blue-700"><strong>Sandata EVV Requirement:</strong> At least one of Full SSN, NPI, or Employee Medicaid ID must be provided for Sandata submission.</p>
            </div>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button type="submit" className="bg-indigo-900 hover:bg-indigo-800" disabled={saving} data-testid="save-employee-btn">
              {saving ? <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" /> : editing ? 'Save Changes' : 'Add Employee'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export { JOB_TITLES, CERTIFICATION_TYPES };
